import type {
    RangeSelectorProps,
    Range,
    MultiSliderProps,
} from '@archipro-design/aria';
import {
    RangeSelector,
    pxToRem,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import type { FilterStatesKey } from '../../type/filter-state';
import { useEffect, useRef, useState } from 'react';

export interface RangeSelectorFilter {
    kind: 'RangeSelector';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
    props: RangeSelectorProps;
    displayTitle?: string;
}

export type RangeSelectorFilterState = Pick<
    RangeSelectorProps,
    'range' | 'value'
> &
    Pick<RangeSelectorFilter, 'kind'>;

interface FilterRangeSelectorProps {
    filter: RangeSelectorFilter;
    state: RangeSelectorFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<RangeSelectorFilterState>,
    ) => void;
    disabled?: boolean;
}

const FilterRangeSelector = ({
    filter,
    state,
    updateFilter,
    disabled,
}: FilterRangeSelectorProps) => {
    const { props, filterKey } = filter;

    const { desktop } = useAppDisplayModeContext();

    const valueChanged = useRef(false);

    const [localState, setLocalState] =
        useState<RangeSelectorFilterState>(state);

    useEffect(() => {
        setLocalState(state);
    }, [state]);

    const updateValue = (value?: Range) => {
        if (!valueChanged.current) return;
        updateFilter(filterKey, { kind: 'RangeSelector', value });
        valueChanged.current = false;
    };

    return (
        <RangeSelector
            {...props}
            disabled={disabled}
            range={localState?.range ?? props.range}
            value={localState.value ?? null}
            onChange={(e, data) => {
                valueChanged.current = true;
                setLocalState({ ...localState, value: data?.value });
                // Update URL if numerical input changes
                if (e.type === 'blur') {
                    updateValue(data?.value);
                }
            }}
            // Update URL on user interaction end events (prevent overfetching)
            multiSlider={{
                onMouseUp: (
                    _: React.SyntheticEvent,
                    data?: Omit<MultiSliderProps, 'value'> & { value: Range },
                ) => {
                    updateValue(data?.value);
                },
                onTouchEnd: (
                    _: React.SyntheticEvent,
                    data?: Omit<MultiSliderProps, 'value'> & { value: Range },
                ) => {
                    updateValue(data?.value);
                },
                variables: {
                    thumbSize: desktop ? pxToRem(20) : pxToRem(28),
                },
            }}
        />
    );
};

export default FilterRangeSelector;
